import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, linkedSignal } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountType } from "@front/m19-api-client";
import { AccountSelectionService, TacosStrategiesService, TacosStrategy } from "@front/m19-services";
import { ToasterService } from "@front/m19-ui";
import { parseIdFromParam } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { SbWizardPageComponent } from "@m19-board/strategies/sb-create-strategy/sb-wizard-page/sb-wizard-page.component";
import { SbWizardCreationService } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-creation.service";
import { createSbWizardForm } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-form";
import {
  algoConfigForTacosTarget,
  assetsDefaultFormModel,
  creativesSbAsinsDefaultFormModel,
  formatDefaultFormModel,
  setupDefaultFormModel,
} from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-form.defaults";
import {
  CREATE_STRATEGY_STEP_IDS,
  SbWizardFormModel,
  TACOS_STRATEGIES_URL,
} from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-types";
import { combineLatest, map, of, switchMap, take, tap } from "rxjs";

@Component({
  selector: "app-sb-tacos-activation-page",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SbWizardCreationService],
  imports: [SbWizardPageComponent],
  template: `
    <app-sb-wizard-page
      [form]="creationForm"
      [steps]="steps"
      [ready]="strategyLoadReady()"
      [tacosSpAsins]="tacosSpAsinList()"
      [disableStoreSpotlight]="true"
      [disableManualCollection]="disableManualCollection()"
      hideSetupNameAndAlgo
      (cancelled)="leave()"
    />
  `,
})
export class SbTacosActivationPage {
  private readonly sbWizardCreationService = inject(SbWizardCreationService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  private readonly isVendor = computed(() => this.accountMarketplace().accountType === AccountType.VENDOR);

  readonly steps = CREATE_STRATEGY_STEP_IDS;

  private readonly tacosStrategyGroupId = toSignal(
    this.route.paramMap.pipe(map((params) => parseIdFromParam(params.get("tacosId")))),
    { initialValue: null },
  );

  private readonly tacosActivationContext = toSignal(
    combineLatest([
      toObservable(this.tacosStrategyGroupId),
      this.accountSelectionService.singleAccountMarketplaceSelection$,
    ]).pipe(
      switchMap(([tacosId, am]) => {
        if (tacosId == null) {
          void this.router.navigate([TACOS_STRATEGIES_URL]);
          return of(null);
        }
        return this.tacosStrategiesService.getTacosStrategies(am.accountId, am.marketplace, tacosId).pipe(
          take(1),
          map((ts: TacosStrategy): TacosStrategy | null => (ts?.spStrategy ? ts : null)),
          tap((context) => {
            if (!context) {
              void this.router.navigate([TACOS_STRATEGIES_URL]);
            }
          }),
        );
      }),
    ),
  );

  readonly strategyLoadReady = computed(() => this.tacosActivationContext() != null);
  readonly tacosSpAsinList = computed(() => {
    const asins = this.tacosActivationContext()?.spStrategy?.asins;
    if (!asins?.length) return undefined;
    return asins.map((a) => a.asin!).filter((asin): asin is string => !!asin);
  });

  protected readonly disableManualCollection = computed(() => (this.tacosSpAsinList() ?? []).length < 3);

  private readonly formModel = linkedSignal<SbWizardFormModel>(() => {
    const tacos = this.tacosActivationContext();
    if (!tacos) {
      return {
        setup: setupDefaultFormModel,
        format: formatDefaultFormModel,
        assets: assetsDefaultFormModel,
        creativesSbAsins: creativesSbAsinsDefaultFormModel,
      };
    }

    return {
      setup: {
        name: tacos.spStrategy!.name ?? "",
        brand: null,
        algo: algoConfigForTacosTarget(),
      },
      format: formatDefaultFormModel,
      assets: assetsDefaultFormModel,
      creativesSbAsins: creativesSbAsinsDefaultFormModel,
    };
  });

  readonly creationForm = createSbWizardForm({
    formModel: this.formModel,
    isVendor: this.isVendor,
    marketplace: computed(() => this.accountMarketplace().marketplace),
    submitForm: async () => this.submit(),
  });

  submit() {
    const tacosId = this.tacosStrategyGroupId();
    if (tacosId == null) return;

    this.sbWizardCreationService
      .createTacosSbStrategy(this.creationForm, this.accountMarketplace(), tacosId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("sb-form-creative.sb_strategy_created"));
          this.leave();
        },
        error: (err) => {
          this.toasterService.error(err);
        },
      });
  }

  leave(): void {
    const tacosId = this.tacosStrategyGroupId();
    if (tacosId != null) {
      void this.router.navigate([TACOS_STRATEGIES_URL, tacosId]);
      return;
    }
    void this.router.navigate([TACOS_STRATEGIES_URL]);
  }
}
